import moment from 'moment';
import { ICopyable } from '../controls/common/icopyable';
import { AttributeOptions } from './attribute-options';
import { AttributeOrder } from './attribute-order';
import { AttributeValue } from './attribute-value';
import { PropertyType } from './enums/property-type';
import { IAttribute } from './interfaces/attribute.interface';
import { ISortable } from './interfaces/sortable.interface';

export class Attribute implements IAttribute, ICopyable<Attribute>, ISortable {
    public id: number = null;
    public organizationId: string = null;
    public name: string = null;
    public type: PropertyType = null;
    public configurationApiId: number = null;
    public format: string = null;
    public displayFormat: number = null;
    public defaultValue: string = null;
    public order: number = null;
    public enable: boolean = true;
    public custom: boolean = true;
    public mandatory: boolean = false;
    public isFavorite: boolean = false;
    public displayedInCreationForm: boolean = true;
    public header: string = null;
    public attributeValueDefinitions: AttributeValue[] = null;
    public attributeOrder: AttributeOrder = null;
    public usedBySearch: boolean = true;
    public invisible: boolean = true;
    public options: AttributeOptions = null;

    // Propriétés utilisées dans le client web
    public defaultValueDisplayed: string = null;

    public createCopy(): Attribute {
        const instance = new Attribute();
        instance.id = this.id;
        instance.organizationId = this.organizationId;
        instance.name = this.name;
        instance.type = this.type;
        instance.configurationApiId = this.configurationApiId;
        instance.format = this.format;
        instance.displayFormat = this.displayFormat;
        instance.defaultValue = this.defaultValue;
        instance.order = this.order;
        instance.enable = this.enable;
        instance.custom = this.custom;
        instance.mandatory = this.mandatory;
        instance.isFavorite = this.isFavorite;
        instance.displayedInCreationForm = this.displayedInCreationForm;
        instance.header = this.header;
        instance.attributeOrder = this.attributeOrder.createCopy();
        instance.usedBySearch = this.usedBySearch;
        instance.invisible = this.invisible;
        instance.options = this.options.createCopy();

        this.attributeValueDefinitions.forEach((x) => {
            const attributeValueDefinition = x.createCopy();
            instance.attributeValueDefinitions.push(attributeValueDefinition);
        });

        return instance;
    }

    public static createFormCopy(attribute: Attribute): IAttribute {
        const instance = new Attribute();
        instance.id = attribute.id;
        instance.organizationId = attribute.organizationId;
        instance.name = attribute.name;
        instance.type = attribute.type;
        instance.configurationApiId = attribute.configurationApiId;
        instance.format = attribute.format;
        instance.displayFormat = attribute.displayFormat;
        instance.defaultValue = attribute.defaultValue;
        instance.order = attribute.order;
        instance.enable = attribute.enable;
        instance.custom = attribute.custom;
        instance.mandatory = attribute.mandatory;
        instance.isFavorite = attribute.isFavorite;
        instance.displayedInCreationForm = attribute.displayedInCreationForm;
        instance.header = attribute.header;
        instance.usedBySearch = attribute.usedBySearch;
        instance.invisible = attribute.invisible;
        instance.attributeValueDefinitions = AttributeValue.fromArray(attribute.attributeValueDefinitions);
        instance.attributeOrder = AttributeOrder.from(attribute.attributeOrder);
        instance.options = AttributeOptions.from(attribute.options);

        return instance;
    }

    public copyFrom(other: any) {
        this.id = other.id;
        this.organizationId = other.organizationId;
        this.name = other.name;
        this.type = other.type;
        this.configurationApiId = other.configurationApiId;
        this.format = other.format;
        this.displayFormat = other.displayFormat;
        this.defaultValue = other.defaultValue;
        this.order = other.order;
        this.enable = other.enable;
        this.custom = other.custom;
        this.mandatory = other.mandatory;
        this.isFavorite = other.isFavorite;
        this.displayedInCreationForm = other.displayedInCreationForm;
        this.header = other.header;
        this.usedBySearch = other.usedBySearch;
        this.invisible = other.invisible;
        this.attributeValueDefinitions = AttributeValue.fromArray(other.attributeValueDefinitions);
        this.attributeOrder = AttributeOrder.from(other.attributeOrder);
        this.options = AttributeOptions.from(other.options);

        switch (this.type) {
            case PropertyType.List:
            case PropertyType.ListCustomers:
                if (this.defaultValue) {
                    this.defaultValueDisplayed = this.attributeValueDefinitions.find((a) => a.id === Number.parseInt(this.defaultValue))?.name;
                }
                break;
            case PropertyType.Date:
            case PropertyType.DateAndTime:
                if (this.defaultValue) {
                    let date = moment(this.defaultValue);
                    this.defaultValueDisplayed = date.format(this.getDisplayFormatDate(this.displayFormat));
                }
                break;
            case PropertyType.PhoneNumber:
                if (this.defaultValue) {
                    this.defaultValueDisplayed = this.getFormatedPhoneNumber(this.defaultValue, this.displayFormat);
                }
                break;
            default:
                this.defaultValueDisplayed = this.defaultValue;
                break;
        }
    }

    public getDisplayName() {
        return `${this.name}`;
    }

    private getDisplayFormatDate(index: number): string {
        switch (index) {
            case 1:
                return 'DD/MM/YYYY';
            case 2:
                return 'YYYY-MM-DD';
            case 3:
                return 'DD/MM/YYYY HH:mm:ss';
            case 4:
                return 'DD/MM/YYYY HH:mm';
            case 5:
                return 'DD/MM/YYYY HH';
            case 6:
                return 'DD/MM';

            default:
                return null;
        }
    }

    private getFormatedPhoneNumber(phoneNumber: string, formatIndex: number): string {
        let parts = this.splitPhoneNumber(phoneNumber);

        switch (formatIndex) {
            case 7:
                // XXXXXXXXXX
                return parts.join('');
            case 8:
                // XX XX XX XX XX
                return parts.join(' ');
            case 9:
                // XX.XX.XX.XX.XX
                return parts.join('.');
            case 10:
                // XX-XX-XX-XX-XX
                return parts.join('-');

            default:
                return null;
        }
    }

    private splitPhoneNumber(phoneNumber: string): string[] {
        let parts: string[] = [];
        let reversePhoneNumber = phoneNumber.split('').reverse().join('');
        parts.push(reversePhoneNumber.substring(0, 2).split('').reverse().join(''));
        reversePhoneNumber = reversePhoneNumber.substring(2);
        parts.push(reversePhoneNumber.substring(0, 2).split('').reverse().join(''));
        reversePhoneNumber = reversePhoneNumber.substring(2);
        parts.push(reversePhoneNumber.substring(0, 2).split('').reverse().join(''));
        reversePhoneNumber = reversePhoneNumber.substring(2);
        parts.push(reversePhoneNumber.substring(0, 2).split('').reverse().join(''));
        reversePhoneNumber = reversePhoneNumber.substring(2);
        if (reversePhoneNumber.length === 2) {
            parts.push(reversePhoneNumber.substring(0, 2).split('').reverse().join(''));
            reversePhoneNumber = reversePhoneNumber.substring(2);
        } else {
            let lastPart = reversePhoneNumber.substring(0, 1).split('').reverse().join('');
            reversePhoneNumber = reversePhoneNumber.substring(1);
            let indicatif = `(${reversePhoneNumber.split('').reverse().join('')})`;
            parts.push(`${indicatif} ${lastPart}`);
        }

        return parts.reverse();
    }
}
